// Syrve Loyalty Synchronization Service
import { getSyrveClient, initializeSyrve } from './syrveClient';
import { fs } from '../firebase';
import { doc, getDoc, updateDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getUserPhoneNumber, normalizePhoneNumber } from '../utils/userUtils';

export const SYRVE_CUSTOMER_COMMENT = 'FastDelivery';

function getLast7Digits(phoneNumber) {
  if (!phoneNumber) return null;
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  return digitsOnly.slice(-7);
}

export function getConsentStatus(userData) {
  return userData && userData.marketingOptIn === true ? 1 : 0;
}

// Sync a Firebase user with Syrve customer
export async function syncUserWithSyrve(userEmail, userData) {
  try {
    const syrve = getSyrveClient();
    await syrve.ensureAuthenticated();

    const rawPhoneNumber = getUserPhoneNumber(userData);
    const phoneNumber = rawPhoneNumber ? normalizePhoneNumber(rawPhoneNumber) : null;
    const last7Digits = getLast7Digits(phoneNumber);
    const cardNumber = last7Digits;
    const cardTrack = userData.cardTrack || last7Digits || "9";

    const customerData = {
      id: null,
      phone: phoneNumber || null,
      cardTrack: cardTrack,
      cardNumber: cardNumber,
      name: userData.name || userData.Name,
      surName: userData.surname || userData.Surname,
      birthday: userData.birthday || userData.Birthday || null,
      email: userEmail,
      userData: userData.personalId,
      consentStatus: getConsentStatus(userData),
      comment: SYRVE_CUSTOMER_COMMENT
    };

    Object.keys(customerData).forEach(key =>
      customerData[key] === undefined && delete customerData[key]
    );

    const syrveResponse = await syrve.createOrUpdateCustomer(customerData);

    if (syrveResponse && syrveResponse.id) {
      console.warn('syncUserWithSyrve: Cannot update without uid. Use enrollUserInLoyaltyProgram instead.');
      return syrveResponse;
    }

    return null;
  } catch (error) {
    console.error(`Error syncing user ${userEmail} with Syrve:`, error);
    throw error;
  }
}

// Get or create Syrve customer from Firebase user
export async function getOrCreateSyrveCustomer(userEmail) {
  try {
    // First check if user already has Syrve ID
    const userRef = doc(fs, 'Users', userEmail);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error('User not found in Firebase');
    }

    const userData = userSnap.data();

    // If already has Syrve ID, verify it still exists
    if (userData.syrveCustomerId) {
      try {
        const syrve = getSyrveClient();
        const customerInfo = await syrve.getCustomerInfo(userData.syrveCustomerId);
        if (customerInfo) {
          return customerInfo;
        }
      } catch (error) {
        console.log('Syrve customer ID invalid, creating new one');
      }
    }

    // Create new Syrve customer
    return await syncUserWithSyrve(userEmail, userData);
  } catch (error) {
    console.error('Error getting/creating Syrve customer:', error);
    throw error;
  }
}

// Sync loyalty card with Syrve
export async function syncLoyaltyCard(userEmail, cardNumber) {
  try {
    const syrve = getSyrveClient();

    // Get or create Syrve customer
    const customer = await getOrCreateSyrveCustomer(userEmail);

    if (!customer || !customer.id) {
      throw new Error('Failed to get Syrve customer');
    }

    // Add card to customer in Syrve
    const cardResponse = await syrve.addCardToCustomer(
      customer.id,
      cardNumber,
      cardNumber // Use card number as track if no magnetic strip
    );

    // Update Firebase with card sync status
    const userRef = doc(fs, 'Users', userEmail);
    await updateDoc(userRef, {
      cardSyncedToSyrve: true,
      cardSyncedAt: new Date()
    });

    console.log(`Card ${cardNumber} synced to Syrve for user ${userEmail}`);
    return cardResponse;
  } catch (error) {
    console.error('Error syncing loyalty card:', error);
    throw error;
  }
}

// Get current points balance from Syrve
export async function getSyrveBalance(userEmail) {
  try {
    const customer = await getOrCreateSyrveCustomer(userEmail);

    if (!customer || !customer.id) {
      return null;
    }

    const syrve = getSyrveClient();
    const walletBalances = await syrve.getWalletBalance(customer.id);

    // Sum all wallet balances (in case of multiple wallets)
    const totalBalance = walletBalances.reduce((sum, wallet) => {
      // Type 1 is bonus program (points)
      if (wallet.type === 1) {
        return sum + (wallet.balance || 0);
      }
      return sum;
    }, 0);

    // Cache balance in Firebase
    const userRef = doc(fs, 'Users', userEmail);
    await updateDoc(userRef, {
      syrveBalance: totalBalance,
      syrveBalanceUpdatedAt: new Date()
    });

    return {
      total: totalBalance,
      wallets: walletBalances
    };
  } catch (error) {
    console.error('Error getting Syrve balance:', error);
    return null;
  }
}

// Award points after order completion
export async function awardOrderPoints(orderId, userEmail, pointsToAward, comment) {
  try {
    const customer = await getOrCreateSyrveCustomer(userEmail);

    if (!customer || !customer.id) {
      throw new Error('Customer not found');
    }

    const syrve = getSyrveClient();

    // Get customer's bonus wallet
    const walletBalances = await syrve.getWalletBalance(customer.id);
    const bonusWallet = walletBalances.find(w => w.type === 1); // Type 1 = bonus

    if (!bonusWallet) {
      throw new Error('Customer has no bonus wallet');
    }

    // Add points to wallet
    const result = await syrve.addPoints(
      customer.id,
      bonusWallet.id,
      pointsToAward,
      comment || `Order ${orderId} completion`
    );

    // Update order with points awarded
    const orderRef = doc(fs, 'DeliveryOrder', orderId);
    await updateDoc(orderRef, {
      pointsAwarded: pointsToAward,
      pointsAwardedAt: new Date(),
      syrveTransactionId: result.transactionId || null
    });

    console.log(`Awarded ${pointsToAward} points for order ${orderId}`);
    return result;
  } catch (error) {
    console.error('Error awarding order points:', error);
    throw error;
  }
}

// Use points for payment
export async function usePointsForPayment(userEmail, pointsToUse, orderId) {
  try {
    const customer = await getOrCreateSyrveCustomer(userEmail);

    if (!customer || !customer.id) {
      throw new Error('Customer not found');
    }

    const syrve = getSyrveClient();

    // Get customer's bonus wallet
    const walletBalances = await syrve.getWalletBalance(customer.id);
    const bonusWallet = walletBalances.find(w => w.type === 1);

    if (!bonusWallet) {
      throw new Error('Customer has no bonus wallet');
    }

    // Check balance
    if (bonusWallet.balance < pointsToUse) {
      throw new Error(`Insufficient points. Available: ${bonusWallet.balance}, Requested: ${pointsToUse}`);
    }

    // Use points
    const result = await syrve.usePoints(
      customer.id,
      bonusWallet.id,
      pointsToUse,
      `Payment for order ${orderId}`
    );

    console.log(`Used ${pointsToUse} points for order ${orderId}`);
    return result;
  } catch (error) {
    console.error('Error using points for payment:', error);
    throw error;
  }
}

// Batch sync all users with cards
export async function batchSyncUsersWithCards() {
  try {
    const syrve = getSyrveClient();
    await syrve.ensureAuthenticated();

    // Get all users with card numbers
    const usersQuery = query(
      collection(fs, 'Users'),
      where('CardNumber', '!=', null)
    );

    const usersSnapshot = await getDocs(usersQuery);
    const results = {
      success: [],
      failed: [],
      skipped: []
    };

    for (const userDoc of usersSnapshot.docs) {
      const userEmail = userDoc.id;
      const userData = userDoc.data();

      // Skip if already synced recently (within last 24 hours)
      if (userData.syrveSyncedAt) {
        const lastSync = userData.syrveSyncedAt.toDate();
        const hoursSinceSync = (Date.now() - lastSync.getTime()) / (1000 * 60 * 60);

        if (hoursSinceSync < 24) {
          results.skipped.push({
            email: userEmail,
            reason: 'Recently synced'
          });
          continue;
        }
      }

      try {
        await syncUserWithSyrve(userEmail, userData);

        if (userData.CardNumber) {
          await syncLoyaltyCard(userEmail, userData.CardNumber);
        }

        results.success.push(userEmail);
      } catch (error) {
        results.failed.push({
          email: userEmail,
          error: error.message
        });
      }

      // Add delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    console.log('Batch sync completed:', results);
    return results;
  } catch (error) {
    console.error('Error in batch sync:', error);
    throw error;
  }
}

export async function getWalletBalancesByCardNumber(cardNumber, phoneNumber = null) {
  try {
    console.log('Fetching wallet balances for card:', cardNumber, 'phone:', phoneNumber);

    if (!phoneNumber || phoneNumber.trim() === '') {
      console.warn('No phone number provided, cannot fetch customer info');
      return null;
    }

    const syrve = await initializeSyrve();

    let customerInfo;

    try {
      customerInfo = await syrve.getCustomerByPhone(phoneNumber);
      console.log('Customer found by phone');
    } catch (error) {
      console.warn('Failed to get customer by phone:', error.message);
      return null;
    }

    if (!customerInfo) {
      console.warn('Customer not found');
      return null;
    }

    if (customerInfo.isDeleted === true) {
      console.warn('⚠️ Customer is marked as deleted in Syrve:', customerInfo.id);
      console.log('🔄 Attempting to reactivate customer via create_or_update');

      try {
        const last7DigitsForReactivation = getLast7Digits(phoneNumber);
        const reactivatedCustomer = await syrve.createOrUpdateCustomer({
          id: null,
          phone: phoneNumber || null,
          cardTrack: customerInfo.cardTrack || last7DigitsForReactivation || "9",
          cardNumber: last7DigitsForReactivation || customerInfo.cardNumber || '',
          name: customerInfo.name || '',
          surName: customerInfo.surname || '',
          birthday: customerInfo.birthday || null,
          email: customerInfo.email || '',
          consentStatus: customerInfo.consentStatus ?? 0,
          comment: SYRVE_CUSTOMER_COMMENT
        });

        console.log('✅ Customer reactivated successfully');

        customerInfo = await syrve.getCustomerByPhone(phoneNumber);
        console.log('📊 Fetched updated customer info after reactivation');
      } catch (error) {
        console.error('❌ Failed to reactivate customer:', error.message);
        return null;
      }
    }

    const walletBalances = customerInfo.walletBalances || [];

    const restaurantMap = {
      'nadimi': ['nadimi', 'nadimi café'],
      'copacabana': ['copacabana', 'copa', 'cabana'],
      'aromiitaliani': ['aromi', 'aromi italiani', 'italiani'],
      'poshnosh': ['posh', 'nosh', 'posh nosh'],
      'symposium': ['symposium', 'sympo'],
      'fastdelivery': ['fast delivery', 'fastdelivery', 'fast_delivery', 'fast', 'sfa-delivery', 'sfa delivery', 'sfa']
    };

    const mappedBalances = {};

    walletBalances.forEach(wallet => {
      if (wallet.type === 1) {
        const walletNameLower = (wallet.name || '').toLowerCase();

        for (const [restaurantKey, variations] of Object.entries(restaurantMap)) {
          if (variations.some(variation => walletNameLower.includes(variation))) {
            mappedBalances[restaurantKey] = Number(wallet.balance) || 0;
            break;
          }
        }

        if (!Object.values(mappedBalances).some(val => val === wallet.balance)) {
          console.log('Unmapped wallet:', wallet.name, 'Balance:', wallet.balance);
        }
      }
    });

    const defaultRestaurants = ['aromiitaliani', 'nadimi', 'copacabana', 'poshnosh', 'symposium', 'fastdelivery'];
    defaultRestaurants.forEach(restaurant => {
      if (!(restaurant in mappedBalances)) {
        mappedBalances[restaurant] = 0;
      }
    });

    console.log('Mapped wallet balances:', mappedBalances);
    return mappedBalances;

  } catch (error) {
    console.error('Error fetching wallet balances:', error);
    return null;
  }
}

export async function enrollUserInLoyaltyProgram(userEmail, userData, programId = null) {
  console.log('=== STARTING SYRVE LOYALTY ENROLLMENT ===');
  console.log('Step 1: Input parameters received');
  console.log('User email:', userEmail);
  console.log('User data:', JSON.stringify(userData, null, 2));
  console.log('Program ID:', programId);

  try {
    console.log('\nStep 2: Initializing Syrve client');
    const syrve = await initializeSyrve();
    console.log('Syrve client initialized, organizationId:', syrve.organizationId);

    console.log('\nStep 3: Already authenticated during initialization');
    console.log('Token status:', syrve.token ? 'EXISTS' : 'MISSING');

    console.log('\nStep 4: Preparing customer data');

    let birthday = null;
    if (userData.dateOfBirth) {
      try {
        const dateObj = new Date(userData.dateOfBirth);
        if (!isNaN(dateObj.getTime())) {
          birthday = dateObj.toISOString().replace('T', ' ').split('.')[0] + '.000';
        } else {
          console.warn('Invalid date of birth:', userData.dateOfBirth);
        }
      } catch (e) {
        console.warn('Failed to parse date of birth:', userData.dateOfBirth, e);
      }
    }

    const rawPhoneNumber = getUserPhoneNumber(userData);
    const phoneNumber = rawPhoneNumber ? normalizePhoneNumber(rawPhoneNumber) : null;
    const last7Digits = getLast7Digits(phoneNumber);
    const cardNumber = last7Digits;
    const cardTrack = userData.cardTrack || last7Digits || "9";

    const customerData = {
      id: null,
      phone: phoneNumber || null,
      cardTrack: cardTrack,
      cardNumber: cardNumber,
      name: userData.firstName || userData.name || userData.Name,
      surName: userData.lastName || userData.surname || userData.Surname,
      birthday,
      email: userEmail,
      userData: userData.personalId,
      consentStatus: getConsentStatus(userData),
      comment: SYRVE_CUSTOMER_COMMENT
    };

    console.log('Customer data before cleanup:', JSON.stringify(customerData, null, 2));

    Object.keys(customerData).forEach(key => {
      if (key === 'id') return;
      const value = customerData[key];
      if (value === undefined || value === null || value === '' || (typeof value === 'string' && value.trim() === '')) {
        delete customerData[key];
      }
    });

    console.log('Customer data after cleanup:', JSON.stringify(customerData, null, 2));

    if (!customerData.phone && !customerData.cardTrack && !customerData.id) {
      console.warn('\n⚠️ ENROLLMENT SKIPPED: No phone number, card track, or customer ID provided');
      console.warn('Syrve API requires at least one of these fields to create a customer');
      console.warn('User will need to provide phone number to complete enrollment');

      return {
        success: false,
        error: 'Phone number required for Syrve enrollment',
        errorDetails: {
          type: 'MissingRequiredField',
          message: 'User must provide phone number to enroll in loyalty program'
        }
      };
    }

    console.log('\nStep 5: Creating/updating customer in Syrve');
    const customerResponse = await syrve.createOrUpdateCustomer(customerData);
    console.log('Customer response received:', JSON.stringify(customerResponse, null, 2));

    if (!customerResponse || !customerResponse.id) {
      console.error('ERROR: Customer response missing or has no ID');
      throw new Error('Failed to create customer in Syrve - no customer ID returned');
    }

    const customerId = customerResponse.id;
    console.log('SUCCESS: Syrve customer created with ID:', customerId);

    console.log('\nStep 6: Determining loyalty program');
    if (!programId) {
      console.log('No program ID provided, fetching available programs...');
      const programsResponse = await syrve.getLoyaltyPrograms();
      console.log('Programs response:', JSON.stringify(programsResponse, null, 2));

      if (programsResponse && programsResponse.Programs && programsResponse.Programs.length > 0) {
        console.log('Available programs:');
        programsResponse.Programs.forEach((prog, index) => {
          console.log(`  ${index + 1}. ${prog.name} (ID: ${prog.id}, Type: ${prog.type || 'unknown'})`);
        });

        // Try to find a "bonus" or "accumulative" program (avoid "discount" programs)
        const bonusProgram = programsResponse.Programs.find(p =>
          p.type === 'Bonus' || p.type === 1 ||
          (p.name && (p.name.toLowerCase().includes('bonus') || p.name.toLowerCase().includes('accumulative')))
        );

        if (bonusProgram) {
          programId = bonusProgram.id;
          console.log('SUCCESS: Using bonus/accumulative program:', programId);
          console.log('Program name:', bonusProgram.name);
        } else {
          // Fall back to first program
          programId = programsResponse.Programs[0].id;
          console.log('WARNING: No bonus program found, using first available program:', programId);
          console.log('Program name:', programsResponse.Programs[0].name);
        }
      } else {
        console.error('ERROR: No loyalty programs available');
        throw new Error('No loyalty programs available in organization');
      }
    } else {
      console.log('Using provided program ID:', programId);
    }

    console.log('\nStep 7: Adding customer to loyalty program');
    console.log('Request parameters:', { customerId, programId, organizationId: syrve.organizationId });

    let programResponse;
    try {
      programResponse = await syrve.addCustomerToProgram(customerId, programId);
      console.log('Program enrollment response:', JSON.stringify(programResponse, null, 2));
      console.log('User wallet ID:', programResponse?.userWalletId || 'NOT PROVIDED');
    } catch (enrollError) {
      console.error('ERROR: Failed to enroll in selected program:', enrollError.message);

      // If the error is about guests not being allowed, try to find another program
      if (enrollError.message.includes('guest') || enrollError.message.includes('Discount')) {
        console.log('Attempting to find alternative loyalty program...');
        const programsResponse = await syrve.getLoyaltyPrograms();

        if (programsResponse && programsResponse.Programs && programsResponse.Programs.length > 1) {
          // Try each program until one works
          for (const program of programsResponse.Programs) {
            if (program.id === programId) continue; // Skip the one that failed

            console.log(`Trying program: ${program.name} (${program.id})`);
            try {
              programResponse = await syrve.addCustomerToProgram(customerId, program.id);
              programId = program.id;
              console.log('SUCCESS: Enrolled in alternative program:', program.name);
              break;
            } catch (altError) {
              console.log(`Failed to enroll in ${program.name}:`, altError.message);
            }
          }
        }

        if (!programResponse) {
          console.error('ERROR: Could not enroll in any available loyalty program');
          throw new Error('Unable to enroll customer in any loyalty program: ' + enrollError.message);
        }
      } else {
        throw enrollError;
      }
    }

    console.log('\nStep 8: Updating Firebase user document');
    const userRef = doc(fs, 'users', userData.uid);
    const updateData = {
      syrveCustomerId: customerId,
      userWalletId: programResponse?.userWalletId || null,
      syrveLoyaltyProgramId: programId,
      cardNumber: cardNumber,
      syrveSyncedAt: new Date(),
      syrveEnrolledAt: new Date(),
      syrveOrgId: syrve.organizationId
    };
    console.log('Updating users/{uid} with:', JSON.stringify(updateData, null, 2));
    await setDoc(userRef, updateData, { merge: true });
    console.log('SUCCESS: User document updated');

    const result = {
      success: true,
      customerId,
      walletId: programResponse?.userWalletId,
      programId
    };
    console.log('\n=== ENROLLMENT COMPLETED SUCCESSFULLY ===');
    console.log('Final result:', JSON.stringify(result, null, 2));
    return result;

  } catch (error) {
    console.error('\n=== ENROLLMENT FAILED ===');
    console.error('Error type:', error.name);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    console.error('Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));

    return {
      success: false,
      error: error.message,
      errorDetails: {
        type: error.name,
        stack: error.stack
      }
    };
  }
}

export async function syncMarketingConsent(uid) {
  try {
    const userSnap = await getDoc(doc(fs, 'users', uid));
    if (!userSnap.exists()) {
      return null;
    }

    const userData = userSnap.data();

    const syrve = await initializeSyrve();

    let birthday = null;
    if (userData.dateOfBirth) {
      try {
        const dateObj = new Date(userData.dateOfBirth);
        if (!isNaN(dateObj.getTime())) {
          birthday = dateObj.toISOString().replace('T', ' ').split('.')[0] + '.000';
        }
      } catch (e) {
        birthday = null;
      }
    }

    const rawPhoneNumber = getUserPhoneNumber(userData);
    const phoneNumber = rawPhoneNumber ? normalizePhoneNumber(rawPhoneNumber) : null;
    const last7Digits = getLast7Digits(phoneNumber);
    const cardNumber = last7Digits;
    const cardTrack = userData.cardTrack || last7Digits || "9";

    if (!userData.syrveCustomerId && !phoneNumber) {
      return null;
    }

    const customerData = {
      id: userData.syrveCustomerId || null,
      phone: phoneNumber || null,
      cardTrack: cardTrack,
      cardNumber: cardNumber,
      name: userData.firstName || userData.name,
      surName: userData.lastName,
      birthday,
      email: userData.email,
      userData: userData.personalId,
      consentStatus: getConsentStatus(userData),
      comment: SYRVE_CUSTOMER_COMMENT
    };

    Object.keys(customerData).forEach(key => {
      if (key === 'id') return;
      const value = customerData[key];
      if (value === undefined || value === null || value === '' || (typeof value === 'string' && value.trim() === '')) {
        delete customerData[key];
      }
    });

    const response = await syrve.createOrUpdateCustomer(customerData);

    if (response && response.id) {
      await setDoc(doc(fs, 'users', uid), {
        syrveCustomerId: response.id,
        syrveSyncedAt: new Date()
      }, { merge: true });
    }

    return response;
  } catch (error) {
    console.error(`Error syncing marketing consent for user ${uid}:`, error);
    return null;
  }
}

export default {
  syncUserWithSyrve,
  getOrCreateSyrveCustomer,
  syncLoyaltyCard,
  getSyrveBalance,
  awardOrderPoints,
  usePointsForPayment,
  batchSyncUsersWithCards,
  enrollUserInLoyaltyProgram,
  getWalletBalancesByCardNumber,
  getConsentStatus,
  syncMarketingConsent
};
